<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        {{ isEditMode ? "Edit HSN Master" : "Add New HSN Master" }}
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ isEditMode ? "Update HSN code details and GST rate information" : "Create a new HSN code with GST rate information for tax compliance" }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-barcode-scan</v-icon>
                      {{ isEditMode ? "Edit HSN Form" : "New HSN Form" }}
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to HSN Masters
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- HSN Master Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-barcode-scan</v-icon>
                    HSN Master Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.hsn_code"
                        label="HSN/SAC Code"
                        placeholder="Enter HSN or SAC code"
                        outlined
                        :rules="hsnCodeRules"
                        :error-messages="errors.hsn_code"
                        class="professional-field"
                        hide-details="auto"
                        required
                        maxlength="50"
                        counter="50"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-barcode-scan</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model.number="form.gst_rate"
                        label="GST Rate (%)"
                        placeholder="Enter GST rate percentage"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        :rules="gstRateRules"
                        :error-messages="errors.gst_rate"
                        class="professional-field"
                        hide-details="auto"
                        required
                        suffix="%"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-percent</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.description"
                        label="HSN Description"
                        placeholder="Enter detailed description of what this HSN code covers"
                        outlined
                        rows="4"
                        :rules="descriptionRules"
                        :error-messages="errors.description"
                        class="professional-field"
                        hide-details="auto"
                        counter="1000"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- GST Rate Helper Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-flash</v-icon>
                    Quick GST Rate Selection
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        :items="commonGSTRates"
                        label="Common GST Rates"
                        placeholder="Select from common GST rates"
                        outlined
                        :error-messages="errors.common_gst_rate"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        @change="setGSTRate"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-flash</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="gst-info-card">
                        <div class="d-flex align-center">
                          <v-icon color="info" class="mr-2">mdi-information</v-icon>
                          <div>
                            <div class="font-weight-medium mb-1">GST Rate Guidelines</div>
                            <div class="text-caption grey--text">
                              0% - Essential items, 5% - Necessities,<br/>
                              12% - Processed foods, 18% - Standard,<br/>
                              28% - Luxury items
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- HSN Master Status Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    HSN Master Status
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-check-circle</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                HSN Master Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the availability status for this HSN master
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="loading"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="submitForm"
                      :loading="loading"
                      :disabled="loading || !valid"
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
                      {{ isEditMode ? "Update" : "Create" }} HSN Master
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import { mapState } from "vuex";
import axios from "@/axios";

export default {
  name: "HsnMasterCreate",
  data() {
    return {
      valid: false,
      loading: false,
      isEditMode: false,

      // Form Data
      form: {
        hsn_code: "",
        description: "",
        gst_rate: null,
        is_active: true,
      },

      // Error handling
      errors: {},

      // Validation Rules
      hsnCodeRules: [
        (v) => !!v || "HSN/SAC Code is required",
        (v) => /^[0-9]+$/.test(v) || "HSN/SAC Code must contain only numbers",
        (v) => (v && v.length >= 4) || "HSN/SAC Code must be at least 4 digits",
        (v) => (v && v.length <= 10) || "HSN/SAC Code must not exceed 10 digits",
      ],

      gstRateRules: [
        (v) => v !== null && v !== undefined && v !== "" || "GST Rate is required",
        (v) => v >= 0 || "GST Rate cannot be negative",
        (v) => v <= 100 || "GST Rate cannot exceed 100%",
        (v) => /^\d+(\.\d{1,2})?$/.test(v.toString()) || "GST Rate can have maximum 2 decimal places",
      ],

      descriptionRules: [
        (v) => !!v || "Description is required",
        (v) => (v && v.length >= 10) || "Description must be at least 10 characters",
        (v) => (v && v.length <= 1000) || "Description must not exceed 1000 characters",
      ],

      // Common GST Rates for quick selection
      commonGSTRates: [
        { text: "0% - Exempt", value: 0 },
        { text: "3% - Gold", value: 3 },
        { text: "5% - Essential items", value: 5 },
        { text: "12% - Processed food, computers", value: 12 },
        { text: "18% - Most goods & services", value: 18 },
        { text: "28% - Luxury items", value: 28 },
      ],
    };
  },

  computed: {
    ...mapState(["user"]),
  },

  async mounted() {
    // Check if this is edit mode
    if (this.$route.params.id) {
      this.isEditMode = true;
      await this.fetchHsnMaster();
    }
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async fetchHsnMaster() {
      this.loading = true;
      try {
        const response = await axios.get(`/hsn-masters/${this.$route.params.id}`);
        const hsnMaster = response.data.data;
        
        this.form = {
          hsn_code: hsnMaster.hsn_code || "",
          description: hsnMaster.description || "",
          gst_rate: hsnMaster.gst_rate,
          is_active: hsnMaster.is_active !== undefined ? hsnMaster.is_active : true,
        };
      } catch (error) {
        console.error("Error fetching HSN Master:", error);
        this.$toast.error("Failed to fetch HSN Master details");
        this.$router.push("/hsn-masters");
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        // Prepare the data
        const formData = {
          hsn_code: this.form.hsn_code,
          description: this.form.description,
          gst_rate: parseFloat(this.form.gst_rate),
          is_active: this.form.is_active,
        };

        if (this.isEditMode) {
          await axios.patch(`/hsn-masters/${this.$route.params.id}`, formData);
          this.$toast.success("HSN Master updated successfully!");
        } else {
          await axios.post("/hsn-masters", formData);
          this.$toast.success("HSN Master created successfully!");
        }
        
        this.$router.push("/hsn-masters");
      } catch (error) {
        console.error("Error saving HSN Master:", error);
        
        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$toast.error("Authentication required. Please login again.");
            this.$router.push("/login");
          } else {
            // Other errors
            this.$toast.error(
              error.response.data.message ||
              `Failed to ${this.isEditMode ? "update" : "create"} HSN Master`
            );
          }
        } else {
          // Network or other errors
          this.$toast.error(
            "Network error. Please check your connection and try again."
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        hsn_code: "",
        description: "",
        gst_rate: null,
        is_active: true,
      };
      this.errors = {};
      this.$refs.form.resetValidation();
    },

    setGSTRate(rate) {
      if (rate !== null && rate !== undefined) {
        this.form.gst_rate = rate;
      }
    },
  },

  watch: {
    // Clear errors when user starts typing
    "form.hsn_code"() {
      if (this.errors.hsn_code) {
        this.$delete(this.errors, "hsn_code");
      }
    },
    "form.gst_rate"() {
      if (this.errors.gst_rate) {
        this.$delete(this.errors, "gst_rate");
      }
    },
    "form.description"() {
      if (this.errors.description) {
        this.$delete(this.errors, "description");
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* GST Info Card */
.gst-info-card {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s ease;
}

.gst-info-card:hover {
  border-color: var(--v-info-base);
  background-color: #e3f2fd;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card,
.gst-info-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>